/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@userzoomlibraries/toolkit-button';
import { Os } from '@userzoomplatform/ua-parser';
import { gql, useQuery } from '@apollo/client';
import { useDevice, DeviceOrientations, StudyFooter } from '@userzoomplatform/pe-study-layout';
import { Language } from '@userzoomplatform/pe-i18n';

import { useToken } from '../../../../hooks';
import i18n, { Namespaces } from '../../../../utils/i18n';
import { trusteSeal } from '../../../../utils/constants';
import { MobileAppQueryResponse } from '../../../../types/pre-study';

type Props = {
	className?: string;
};

const defaultProps = {
	className: '',
};

type Badge = {
	className: string;
	src: (languageCode: string) => string;
	alt: string;
};

const badges = {
	appStore: {
		className: 'app-store-badge',
		src: (languageCode: string) => `${process.env.PUBLIC_URL}/images/app-store-badges/${languageCode}.svg`,
		alt: 'App store installation button',
	},
	googlePlay: {
		className: 'google-play-badge',
		src: (languageCode: string) => `${process.env.PUBLIC_URL}/images/google-play-badges/${languageCode}.svg`,
		alt: 'Google Play installation button',
	},
};

const mobileAppQuery = gql`
	query mobileApp {
		mobileApp {
			os
			name
			iosInstallationLink
			androidInstallationLink
			startUrls {
				startUrl
				androidStartUrl
			}
		}
	}
`;

const language = new Language(i18n);

const InstallApp = ({ className }: Props): ReactElement => {
	const { t } = useTranslation([Namespaces.Common, Namespaces.Pscl]);
	const history = useHistory();
	const token = useToken();
	const { device, orientation } = useDevice();
	const [startUrl, setStartUrl] = useState<string>();
	const [installationLink, setInstallationLink] = useState<string | undefined>();
	const [badge, setBadge] = useState<Badge>();

	const { error, loading, data: { mobileApp } = {} } = useQuery<MobileAppQueryResponse>(mobileAppQuery);

	const getInstallationLink = useCallback(
		(os: string | undefined): string => {
			if (mobileApp) {
				const { iosInstallationLink, androidInstallationLink } = mobileApp;
				return os === Os.Ios ? iosInstallationLink : androidInstallationLink;
			}
			return '';
		},
		[mobileApp]
	);

	useEffect(() => {
		if (error) {
			history.replace('/forbidden');
		} else if (mobileApp && device) {
			const {
				startUrls: { androidStartUrl, startUrl: defaultUrl },
			} = mobileApp;
			setStartUrl(device.os?.name === Os.Android ? androidStartUrl : defaultUrl);
			setBadge(device.os?.name === Os.Ios ? badges.appStore : badges.googlePlay);
			setInstallationLink(getInstallationLink(mobileApp.os ?? device.os?.name));
		}
	}, [device, error, history, mobileApp, token, getInstallationLink]);

	const goToStartUrl = useCallback(() => {
		if (startUrl) {
			window.location.href = startUrl;
		}
	}, [startUrl]);

	return !loading && mobileApp ? (
		<div className={`${className} ${orientation === DeviceOrientations.Landscape ? 'landscape' : 'portrait'}`}>
			<img src={`${process.env.PUBLIC_URL}/images/shapes/semi-circle.svg`} alt="Decorative shape" className="shape semi-circle" />
			<img src={`${process.env.PUBLIC_URL}/images/shapes/semi-doughnut.svg`} alt="Decorative shape" className="shape semi-doughnut" />
			<div className="container">
				<div className="content">
					<h1 className="title">{t(`${Namespaces.Pscl}:installApp.title`)}</h1>
					<p className="subtitle">{t(`${Namespaces.Pscl}:installApp.invited`)}</p>
					<div className="cards">
						<div className="card install-card">
							<div className="text">
								<p className="step-number">{t(`${Namespaces.Pscl}:installApp.stepNumberOne`)}</p>
								<h2>{t(`${Namespaces.Pscl}:installApp.appName`, { appName: mobileApp?.name })}</h2>
							</div>
							{installationLink && badge && (
								<a href={installationLink} target="_blank" rel="noopener noreferrer" className="cta">
									<img className={badge?.className} src={badge?.src(language.getLanguage())} alt={badge?.alt} />
								</a>
							)}
						</div>
						<div className="card open-card">
							<div hidden id="startUrl">
								{startUrl?.replace('intent://', '')}
							</div>
							<div className="text">
								<p className="step-number">{t(`${Namespaces.Pscl}:installApp.stepNumberTwo`)}</p>
								<h2>{t(`${Namespaces.Pscl}:installApp.openInApp`)}</h2>
							</div>
							<Button size="lg" className="cta" onClick={goToStartUrl}>
								{t(`${Namespaces.Pscl}:installApp.openStudyCta`)}
							</Button>
						</div>
					</div>
				</div>
				<div className="footer">
					<a href={trusteSeal.link} target="_blank" rel="noopener noreferrer" className="truste-seal">
						<img src={trusteSeal.image} alt="Truste seal" />
					</a>
					<StudyFooter />
				</div>
			</div>
		</div>
	) : (
		<div />
	);
};

InstallApp.defaultProps = defaultProps;

export default InstallApp;
